/**
 * EST-L4/L7 (2026-07-22) — the Kohrt-style two-column scope table used
 * by every CUSTOMER-facing surface (proposal / contract modes of
 * Section III, and Section II's Project Detail block):
 *
 *   - shaded group bars from the statements' groupLabel
 *   - one `label | selection` row per statement-engine line
 *   - consolidated selections only (the statement engine already emits
 *     one combined "Decking Selection: …" line per cascade)
 *   - no SKU nest, no ⓘ, no edit affordances — estimate-only per EST-L1/L3
 *
 * Statement TEXT is never altered beyond stripping a leading
 * "<Label>:" prefix that duplicates the row label (defect #4 from the
 * 2026-07-23 screenshot round).
 */

import { paperCsfLabel, splitPaperStatementLabel } from '@/lib/paper-display';

export interface ScopeStatementLine {
  scfCode?: string | null;
  groupLabel?: string | null;
  text: string;
  source?: string | null;
}

export interface ScopeLabelSource {
  scfCode: string;
  scfLabel: string;
  contractLabel?: string | null;
}

/**
 * Split "Label: value" statement text against the known field label.
 * RULE sentences without the prefix keep their full text in the value
 * cell (label cell still shows the field label when known).
 */
export function splitStatementLabel(
  text: string,
  fieldLabel: string | null,
  aliases: Array<string | null | undefined> = [],
): { label: string | null; value: string } {
  return splitPaperStatementLabel(text, fieldLabel, aliases);
}

export function ScopeStatementsTable({
  statements,
  fields,
  singleBarLabel,
  testId,
}: {
  statements: ScopeStatementLine[];
  fields?: ScopeLabelSource[];
  /**
   * When set, every statement renders under ONE shaded bar with this
   * label (Section II's "PROJECT DETAIL" idiom) instead of the
   * per-group bars.
   */
  singleBarLabel?: string;
  testId?: string;
}) {
  if (statements.length === 0) return null;

  const labelByCode = new Map<string, string>();
  const sourceLabelByCode = new Map<string, string>();
  for (const f of fields ?? []) {
    if (f.scfCode && !labelByCode.has(f.scfCode)) {
      const display = paperCsfLabel(f) || f.scfLabel;
      if (display) labelByCode.set(f.scfCode, display);
    }
    if (f.scfCode && f.scfLabel && !sourceLabelByCode.has(f.scfCode)) {
      sourceLabelByCode.set(f.scfCode, f.scfLabel);
    }
  }

  // Polish round 2026-07-23 #1 — statements are BUCKETED by groupLabel
  // (first-appearance order), not by adjacency runs, so a statement
  // whose displayOrder interleaves other groups still renders under its
  // own bar; null-group statements collect under a trailing OTHER bar.
  // Orphans above the first bar are structurally impossible.
  const rows: Array<
    | { kind: 'bar'; label: string }
    | { kind: 'row'; label: string | null; value: string; scfCode: string | null }
  > = [];
  const toRow = (s: ScopeStatementLine) => {
    const { label, value } = splitStatementLabel(
      s.text,
      s.scfCode ? (labelByCode.get(s.scfCode) ?? null) : null,
      s.scfCode ? [sourceLabelByCode.get(s.scfCode)] : [],
    );
    return { kind: 'row' as const, label, value, scfCode: s.scfCode ?? null };
  };
  if (singleBarLabel) {
    rows.push({ kind: 'bar', label: singleBarLabel });
    for (const s of statements) rows.push(toRow(s));
  } else {
    const buckets: Array<{ label: string | null; items: ScopeStatementLine[] }> = [];
    const indexByLabel = new Map<string, number>();
    let otherIndex = -1;
    for (const s of statements) {
      const label = s.groupLabel ?? null;
      if (label == null) {
        if (otherIndex < 0) {
          otherIndex = buckets.length;
          buckets.push({ label: null, items: [] });
        }
        buckets[otherIndex].items.push(s);
        continue;
      }
      let i = indexByLabel.get(label);
      if (i == null) {
        i = buckets.length;
        indexByLabel.set(label, i);
        buckets.push({ label, items: [] });
      }
      buckets[i].items.push(s);
    }
    if (otherIndex >= 0 && otherIndex !== buckets.length - 1) {
      const [other] = buckets.splice(otherIndex, 1);
      buckets.push(other);
    }
    for (const bucket of buckets) {
      rows.push({ kind: 'bar', label: (bucket.label ?? 'Other').toUpperCase() });
      for (const s of bucket.items) rows.push(toRow(s));
    }
  }

  return (
    <div className="overflow-x-auto">
      <table
        className="w-full border-collapse font-public-sans text-sm"
        data-testid={testId ?? 'scope-statements-table'}
      >
        <tbody>
          {rows.map((r, i) =>
            r.kind === 'bar' ? (
              <tr key={`bar-${i}`} className="block sm:table-row">
                <td
                  colSpan={2}
                  className="block w-full border border-border bg-primary px-3 py-1.5 font-public-sans text-xs font-bold uppercase tracking-[0.06em] text-primary-foreground sm:table-cell"
                >
                  {r.label}
                </td>
              </tr>
            ) : (
              // Micro round 2026-07-23 #2 — rows stack at <sm (label
              // line, value line beneath) so the table fits 390px
              // without clipping; sm+ keeps the two-column layout.
              <tr
                key={`row-${i}`}
                data-statement-code={r.scfCode ?? undefined}
                className="block sm:table-row"
              >
                {r.label != null ? (
                  <>
                    <td className="block w-full border border-b-0 border-border bg-muted/40 px-3 py-1.5 align-top font-semibold text-foreground sm:table-cell sm:w-1/3 sm:border-b sm:py-2">
                      {r.label}
                    </td>
                    <td className="block w-full border border-t-0 border-border px-3 py-1.5 align-top text-foreground sm:table-cell sm:border-t sm:py-2">
                      {r.value}
                    </td>
                  </>
                ) : (
                  <td
                    colSpan={2}
                    className="block w-full border border-border px-3 py-2 align-top text-foreground sm:table-cell"
                  >
                    {r.value}
                  </td>
                )}
              </tr>
            ),
          )}
        </tbody>
      </table>
    </div>
  );
}
